/**
 * PRENDER O MENU DO ITEM DENTRO DA JANELA.
 *
 * Esta conta mora fora de `DraggableItem` por um motivo só: para poder ser testada. Dentro da
 * classe ela depende de um item do Konva, de um `<div>` medido e de um `window` — e a propriedade
 * que importa não é sobre nenhum dos três, é sobre **quatro números e duas bordas**.
 *
 * ── POR QUE ELA EXISTE (`T4.7`) ──────────────────────────────────────────────────────────────
 *
 * O menu do item é onde moram apagar, duplicar, espelhar, as duas camadas e as cores. Ele ficava
 * centrado **acima** do item, e nada o impedia de sair da tela. Com botões de 20px o estrago era
 * pequeno; ao levá-los para os 44px da régua do produto, passou a aparecer em dois casos que
 * acontecem o tempo todo: item colado no alto da folha, e item colado numa borda lateral.
 *
 * **Um menu meio fora da tela não é só feio — os botões que sobram fora não se clicam.** Quem
 * pusesse uma personagem no canto perderia o apagar, o duplicar e as camadas, e concluiria, de
 * novo, que "não tem controle de camadas".
 */

export interface PosicaoDoMenu {
  left: number;
  top: number;
}

/** Espaço mínimo entre o menu e a borda da janela. */
export const MARGEM = 8;
/** Espaço entre o menu e o item que ele serve. */
export const RESPIRO = 10;

/**
 * Onde desenhar o menu, dado onde ele *gostaria* de ficar.
 *
 * @param topoDoItem     o meio do lado de cima do item, em coordenadas de tela
 * @param baseDoItem     o `y` do ponto mais baixo do item — usado quando não cabe em cima
 * @param menu           largura e altura do menu, já medidas
 * @param janela         largura e altura da área visível
 */
export function prenderNaJanela(
  topoDoItem: PosicaoDoMenu,
  baseDoItem: number,
  menu: { largura: number; altura: number },
  janela: { largura: number; altura: number }
): PosicaoDoMenu {
  let left = topoDoItem.left - menu.largura / 2;
  let top = topoDoItem.top - menu.altura - RESPIRO;

  // HORIZONTAL — preso entre as duas bordas.
  //
  // O `Math.max` vem DEPOIS do `Math.min` de propósito: numa janela mais estreita que o próprio
  // menu, as duas travas se contradizem, e quem ganha é a da esquerda. É de onde se lê, e é onde
  // ficam os botões que mais se usam (apagar e duplicar são os dois primeiros).
  left = Math.max(MARGEM, Math.min(left, janela.largura - menu.largura - MARGEM));

  // VERTICAL — se não cabe em cima, vai para BAIXO do item.
  //
  // Nunca POR CIMA dele: o menu tapando o que a criança está ajustando é exatamente o defeito
  // que este conserto existe para não criar.
  if (top < MARGEM) top = baseDoItem + RESPIRO;

  // E se nem embaixo couber — item que ocupa a tela inteira —, encosta no fim da janela.
  top = Math.min(top, janela.altura - menu.altura - MARGEM);
  top = Math.max(MARGEM, top);

  return { left, top };
}
